/**
 * Fullscreen image viewer rendered into document.body via a portal. On desktop
 * (md+) it shows all imageUrls in a horizontally scrollable snap row; on mobile
 * it shows one image at a time with prev/next buttons and a counter. Supports
 * Escape to close and arrow keys to navigate, and locks body scroll while open.
 * Opened from BountyDisplay and SubmissionDisplay when a listing/submission
 * image is clicked.
 */
import { X } from "lucide-react";
import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";

interface ImageCarouselProps {
    imageUrls: string[];
    onClose: () => void;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({ imageUrls, onClose }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    const nextImage = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrentIndex((prev) => (prev < imageUrls.length - 1 ? prev + 1 : prev));
    };

    const prevImage = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
    };

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            } else if (event.key === "ArrowRight") {
                setCurrentIndex((prev) => (prev < imageUrls.length - 1 ? prev + 1 : prev));
            } else if (event.key === "ArrowLeft") {
                setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "hidden";

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            document.body.style.overflow = "";
        };
    }, [onClose, imageUrls.length]);

    const modal = (
        <div
            className="fixed inset-0 bg-black flex items-center justify-center"
            style={{ zIndex: 99999 }}
        >
            {/* Close button */}
            <button
                className="absolute top-4 left-4 p-2 rounded-full bg-black/40 hover:bg-black/60 transition-colors"
                onClick={onClose}
                style={{ zIndex: 100000 }}
            >
                <X className="w-6 h-6 text-white" />
            </button>

            {/* Main carousel container */}
            <div className="w-full h-full flex items-center justify-center p-4">
                {/* Desktop: Show all images in a scrollable row */}
                <div className="hidden md:flex overflow-x-auto snap-x snap-mandatory p-4 space-x-8 max-w-full">
                    {imageUrls.map((url, index) => (
                        <div key={`${index}_div`} className="flex-shrink-0 snap-center">
                            <img
                                key={`${index}_img`}
                                src={url}
                                alt={`Image ${index + 1}`}
                                className="object-contain max-h-[85vh] rounded-lg transition-transform duration-300"
                                loading="lazy"
                            />
                        </div>
                    ))}
                </div>

                {/* Mobile: Show single image with prev/next controls */}
                <div className="flex md:hidden items-center justify-center w-full h-full">
                    {imageUrls.length > 1 && currentIndex > 0 && (
                        <button
                            onClick={prevImage}
                            className="absolute left-2 p-2 rounded-full bg-black/40"
                        >
                            <span className="text-white text-xl">&lt;</span>
                        </button>
                    )}

                    <img
                        src={imageUrls[currentIndex]}
                        alt={`Image ${currentIndex + 1}`}
                        className="max-w-full max-h-[85vh] object-contain rounded-lg"
                    />

                    {imageUrls.length > 1 && currentIndex < imageUrls.length - 1 && (
                        <button
                            onClick={nextImage}
                            className="absolute right-2 p-2 rounded-full bg-black/40"
                        >
                            <span className="text-white text-xl">&gt;</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Image counter for mobile */}
            {imageUrls.length > 1 && (
                <div className="absolute bottom-4 left-0 right-0 md:hidden text-center text-white">
                    {currentIndex + 1} / {imageUrls.length}
                </div>
            )}
        </div>
    );

    return createPortal(modal, document.body);
};

export default ImageCarousel;
